import { ActionMenu, FOCUS_RING, Icon, Pills, type OptionProps } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import DeleteValueDialog from './DeleteValueDialog';
import { resolveCompanyValueIconBackground } from '../../utils/companyValueIconBackground';
import { getIconSvg } from '../../utils/iconSvgs';

import type { CompanyValue } from '@/types/company-values';

import { useToggleCompanyValueStatus } from '@/api/company-values';
import { getLocalizedText } from '@/utils/localization';

interface ValueCardProps {
    value: CompanyValue;
    onEdit?: () => void;
    onDelete?: () => void;
}

export default function ValueCard({ value, onEdit, onDelete }: ValueCardProps) {
    const { t } = useTranslation();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const toggleStatusMutation = useToggleCompanyValueStatus();

    const displayName = getLocalizedText(value.name);
    const displayDescription = getLocalizedText(value.description);

    const handleToggleStatus = async () => {
        try {
            await toggleStatusMutation.mutateAsync(value.id);
            const action = value.status === 'Active' ? 'deactivated' : 'activated';
            toast.success(t('visionValues.toggleStatusSuccess', { name: displayName, action }));
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : t('visionValues.toggleStatusError');
            toast.error(errorMessage);
        }
    };

    const iconBackground = resolveCompanyValueIconBackground(value.icon, value.iconBackground);

    const menuItems: OptionProps[] = [
        {
            id: 'edit',
            label: t('visionValues.edit'),
            textSize: 'lg',
            onClick: () => onEdit?.(),
            icon: (
                <Icon
                    name={'edit-edit-2'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
        {
            id: 'toggle-status',
            label:
                value.status === 'Active'
                    ? t('visionValues.deactivate')
                    : t('visionValues.activate'),
            textSize: 'lg',
            onClick: handleToggleStatus,
            icon: (
                <Icon
                    name={value.status === 'Active' ? 'essential-slash' : 'essential-checkmark'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
        {
            id: 'delete',
            label: t('visionValues.delete'),
            textSize: 'lg',
            onClick: () => setIsDeleteDialogOpen(true),
            danger: true,
            icon: (
                <Icon
                    name={'essential-trash'}
                    size={'sm'}
                    color={'inherit'}
                />
            ),
        },
    ];

    const openEditor = () => onEdit?.();

    // A click anywhere on the card opens the editor; the keyboard and assistive technology reach it
    // through the button on the name, so the card never wraps the kebab menu inside a control of
    // its own. The delete dialog is a sibling of the tile: a click inside it never reaches the card.
    return (
        <>
            <div
                className={
                    'relative flex cursor-pointer flex-col gap-6 rounded-xl bg-white/60 p-5 backdrop-blur-2xl transition-shadow hover:shadow-lg sm:p-7'
                }
                onClick={openEditor}
                data-testid={`value-card-${value.id}`}
            >
                {/* Second in the markup, so the name is read right after the icon. */}
                <div className={'flex flex-wrap items-start justify-between gap-4'}>
                    <div
                        className={
                            'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl p-3 shadow-lg'
                        }
                        style={{ background: iconBackground }}
                    >
                        {getIconSvg(value.icon || 'heart', 40)}
                    </div>

                    <h3 className={'text-navy order-last w-full text-left text-[20px] font-medium'}>
                        <button
                            type={'button'}
                            onClick={(event) => {
                                event.stopPropagation();
                                openEditor();
                            }}
                            className={`${FOCUS_RING} cursor-pointer text-left wrap-break-word`}
                            data-testid={`value-card-${value.id}-name`}
                        >
                            {displayName}
                        </button>
                    </h3>

                    <div className={'flex flex-shrink-0 items-start gap-3'}>
                        <Pills
                            variant={value.status === 'Active' ? 'activeLight' : 'inactiveLight'}
                            status={value.status === 'Active' ? 'active' : 'inactive'}
                            size={'sm'}
                            className={'py-1.5'}
                        >
                            {value.status === 'Active'
                                ? t('visionValues.active')
                                : t('visionValues.inactive')}
                        </Pills>

                        <div onClick={(event) => event.stopPropagation()}>
                            <ActionMenu
                                items={menuItems}
                                ariaLabel={t('visionValues.moreOptions')}
                                variant={'subtle'}
                                size={'sm'}
                                className={'!rounded-md border-gray-300 bg-white'}
                            />
                        </div>
                    </div>
                </div>

                <p className={'text-[13px] font-normal text-gray-500'}>{displayDescription}</p>
            </div>

            <DeleteValueDialog
                isOpen={isDeleteDialogOpen}
                value={value}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onSuccess={() => {
                    setIsDeleteDialogOpen(false);
                    onDelete?.();
                }}
            />
        </>
    );
}
